import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest, apiUpload } from 'utils/api';

// ==============================|| INTERFACES ||============================== //

interface ChatSession {
  id: string;
  projectId: string;
  organizationId: string;
  userId: string;
  title: string | null;
  status: string;
  lastActivityAt: string;
  messageCount: number;
  totalTokens: number;
  totalAcuCost: number;
  metadata: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
}

interface ChatMessage {
  id: string;
  sessionId: string;
  role: string;
  content: string;
  status: string;
  sequenceNumber: number;
  metadata: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
}

interface ChatError {
  message: string;
  activeCount?: number;
  maxConcurrent?: number;
}

interface ChatState {
  sessions: ChatSession[];
  currentSession: ChatSession | null;
  messages: ChatMessage[];
  status: 'idle' | 'loading' | 'success' | 'failed';
  sendStatus: 'idle' | 'loading' | 'success' | 'failed';
  approveStatus: 'idle' | 'loading' | 'success' | 'failed';
  approvingTicketKey: string | null; // Tracks which specific ticket is being approved (messageId-ticketIndex)
  resumeStatus: 'idle' | 'loading' | 'success' | 'failed';
  uploadProgress: number; // 0-100 upload progress for file attachments
  error: ChatError | null;
}

// Helper: API responses are wrapped as { response, metadata } by the transform interceptor
const unwrap = (payload: any) => payload?.response ?? payload?.data ?? payload;

// ==============================|| INITIAL STATE ||============================== //

const initialState: ChatState = {
  sessions: [],
  currentSession: null,
  messages: [],
  status: 'idle',
  sendStatus: 'idle',
  approveStatus: 'idle',
  approvingTicketKey: null,
  resumeStatus: 'idle',
  uploadProgress: 0,
  error: null,
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getChatSessions = createAsyncThunk(
  'chat/getChatSessions',
  async ({ projectId }: { projectId: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/chat/projects/${projectId}/sessions`);
      return response;
    } catch (error: any) {
      console.error('Fetch chat sessions error:', error);
      return rejectWithValue('Failed to fetch chat sessions');
    }
  }
);

export const createChatSession = createAsyncThunk(
  'chat/createChatSession',
  async ({ projectId, title, persona }: { projectId: string; title?: string; persona?: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/chat/projects/${projectId}/sessions`, {
        method: 'POST',
        body: JSON.stringify({ title, persona }),
      });
      return response;
    } catch (error: any) {
      console.error('Create chat session error:', error);
      return rejectWithValue(error?.message || 'Failed to create chat session');
    }
  }
);

export const getChatSession = createAsyncThunk(
  'chat/getChatSession',
  async ({ projectId, sessionId }: { projectId: string; sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/chat/projects/${projectId}/sessions/${sessionId}`);
      return response;
    } catch (error: any) {
      console.error('Fetch chat session error:', error);
      return rejectWithValue('Failed to fetch chat session');
    }
  }
);

export const getChatMessages = createAsyncThunk(
  'chat/getChatMessages',
  async ({ projectId, sessionId, after }: { projectId: string; sessionId: string; after?: string }, { rejectWithValue }) => {
    try {
      const query = after ? `?after=${encodeURIComponent(after)}` : '';
      const response = await apiRequest(`/chat/projects/${projectId}/sessions/${sessionId}/messages${query}`);
      return response;
    } catch (error: any) {
      console.error('Fetch chat messages error:', error);
      return rejectWithValue('Failed to fetch messages');
    }
  }
);

export const sendChatMessage = createAsyncThunk(
  'chat/sendChatMessage',
  async (
    { projectId, sessionId, content, type, files, onProgress }: {
      projectId: string;
      sessionId: string;
      content: string;
      type?: string;
      files?: File[];
      onProgress?: (percent: number) => void;
    },
    { rejectWithValue }
  ) => {
    try {
      if (files && files.length > 0) {
        // Multipart upload path
        const formData = new FormData();
        formData.append('content', content);
        if (type) formData.append('type', type);
        for (const file of files) {
          formData.append('files', file);
        }
        const response = await apiUpload(
          `/chat/projects/${projectId}/sessions/${sessionId}/messages`,
          formData,
          onProgress,
        );
        return response;
      }
      // Standard JSON path
      const response = await apiRequest(`/chat/projects/${projectId}/sessions/${sessionId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ content, type }),
      });
      return response;
    } catch (error: any) {
      console.error('Send chat message error:', error);
      return rejectWithValue(error?.message || 'Failed to send message');
    }
  }
);

export const approveTicket = createAsyncThunk(
  'chat/approveTicket',
  async (
    { projectId, sessionId, messageId, overrides }: {
      projectId: string;
      sessionId: string;
      messageId: string;
      overrides?: Record<string, any>;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiRequest(
        `/chat/projects/${projectId}/sessions/${sessionId}/approve-ticket/${messageId}`,
        {
          method: 'POST',
          body: JSON.stringify(overrides || {}),
        }
      );
      return response;
    } catch (error: any) {
      console.error('Approve ticket error:', error);
      return rejectWithValue(error?.message || 'Failed to create ticket');
    }
  }
);

export const endChatSession = createAsyncThunk(
  'chat/endChatSession',
  async ({ projectId, sessionId }: { projectId: string; sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/chat/projects/${projectId}/sessions/${sessionId}/end`, {
        method: 'POST',
      });
      return response;
    } catch (error: any) {
      console.error('End chat session error:', error);
      return rejectWithValue(error?.message || 'Failed to end session');
    }
  }
);

export const resumeChatSession = createAsyncThunk(
  'chat/resumeChatSession',
  async ({ projectId, sessionId }: { projectId: string; sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/chat/projects/${projectId}/sessions/${sessionId}/resume`, {
        method: 'POST',
      });
      return response;
    } catch (error: any) {
      console.error('Resume chat session error:', error);
      return rejectWithValue(error?.message || 'Failed to resume session');
    }
  }
);

export const deleteChatSession = createAsyncThunk(
  'chat/deleteChatSession',
  async ({ projectId, sessionId }: { projectId: string; sessionId: string }, { rejectWithValue }) => {
    try {
      await apiRequest(`/chat/projects/${projectId}/sessions/${sessionId}`, {
        method: 'DELETE',
      });
      return sessionId;
    } catch (error: any) {
      console.error('Delete chat session error:', error);
      return rejectWithValue(error?.message || 'Failed to delete session');
    }
  }
);

export const updateChatPersona = createAsyncThunk(
  'chat/updateChatPersona',
  async (
    { projectId, sessionId, persona }: { projectId: string; sessionId: string; persona: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiRequest(
        `/chat/projects/${projectId}/sessions/${sessionId}/persona`,
        {
          method: 'PATCH',
          body: JSON.stringify({ persona }),
        }
      );
      return response;
    } catch (error: any) {
      console.error('Update persona error:', error);
      return rejectWithValue(error?.message || 'Failed to update persona');
    }
  }
);

// ==============================|| SLICE ||============================== //

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    clearChatError(state) {
      state.error = null;
    },
    clearCurrentSession(state) {
      state.currentSession = null;
      state.messages = [];
    },
    clearChatState() {
      return initialState;
    },
    setCurrentSession(state, action) {
      state.currentSession = action.payload;
    },
    // Merge polled messages into existing list (dedup by id)
    mergePolledMessages(state, action) {
      const newMessages: ChatMessage[] = action.payload;
      if (newMessages.length === 0) return;

      // Build index map for O(1) lookups
      const indexById = new Map<string, number>();
      for (let i = 0; i < state.messages.length; i++) {
        indexById.set(state.messages[i].id, i);
      }

      let added = false;
      for (const msg of newMessages) {
        const existingIdx = indexById.get(msg.id);
        if (existingIdx !== undefined) {
          state.messages[existingIdx] = msg;
        } else {
          state.messages.push(msg);
          added = true;
        }
      }

      // Only sort when new messages were added (updates preserve order)
      if (added) {
        state.messages.sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0));
      }
    },
    setUploadProgress(state, action) {
      state.uploadProgress = action.payload;
    },
    // Update session status from SSE event
    updateSessionStatus(state, action) {
      const session = action.payload;
      if (state.currentSession?.id === session.id) {
        state.currentSession = { ...state.currentSession, ...session };
      }
      const idx = state.sessions.findIndex(s => s.id === session.id);
      if (idx !== -1) {
        state.sessions[idx] = { ...state.sessions[idx], ...session };
      }
    },
  },
  extraReducers: (builder) => {
    // Get Sessions
    builder
      .addCase(getChatSessions.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getChatSessions.fulfilled, (state, action) => {
        state.status = 'success';
        const data = unwrap(action.payload);
        state.sessions = Array.isArray(data) ? data : [];

        // Race-condition guard: if currentSession was just created but the GET response
        // was sent before the POST was committed, ensure it stays in the list.
        if (state.currentSession) {
          const found = state.sessions.find((s: ChatSession) => s.id === state.currentSession!.id);
          if (found) {
            // Sync currentSession with the latest server data
            state.currentSession = found;
          } else if (state.currentSession.status === 'active') {
            // Session was just created and not in the stale response — keep it
            state.sessions.unshift(state.currentSession);
          }
        }
      })
      .addCase(getChatSessions.rejected, (state, action) => {
        state.status = 'failed';
        state.error = { message: action.payload as string };
      });

    // Create Session
    builder
      .addCase(createChatSession.pending, (state) => {
        state.sendStatus = 'loading';
        state.error = null;
      })
      .addCase(createChatSession.fulfilled, (state, action) => {
        state.sendStatus = 'success';
        const session = unwrap(action.payload);
        state.currentSession = session;
        state.sessions.unshift(session);
        state.messages = [];
      })
      .addCase(createChatSession.rejected, (state, action) => {
        state.sendStatus = 'failed';
        state.error = { message: action.payload as string };
      });

    // Get Session
    builder
      .addCase(getChatSession.fulfilled, (state, action) => {
        state.currentSession = unwrap(action.payload);
      });

    // Get Messages — full load only (incremental uses mergePolledMessages)
    builder
      .addCase(getChatMessages.fulfilled, (state, action) => {
        const data = unwrap(action.payload);
        const messages = Array.isArray(data) ? data : [];
        // If the thunk was called with `after` param, merge incrementally
        const after = (action.meta.arg as any)?.after;
        if (after && messages.length > 0) {
          // Incremental: merge new/updated messages with O(1) lookups
          const indexById = new Map<string, number>();
          for (let i = 0; i < state.messages.length; i++) {
            indexById.set(state.messages[i].id, i);
          }

          let added = false;
          for (const msg of messages) {
            const existingIdx = indexById.get(msg.id);
            if (existingIdx !== undefined) {
              state.messages[existingIdx] = msg;
            } else {
              state.messages.push(msg);
              added = true;
            }
          }

          if (added) {
            state.messages.sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0));
          }
        } else if (!after) {
          // Full load
          state.messages = messages;
        }
      });

    // Send Message
    builder
      .addCase(sendChatMessage.pending, (state) => {
        state.sendStatus = 'loading';
        state.uploadProgress = 0;
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        state.sendStatus = 'success';
        state.uploadProgress = 0;
        const data = unwrap(action.payload);
        const { userMessage, assistantMessage } = data;
        if (userMessage) state.messages.push(userMessage);
        if (assistantMessage) state.messages.push(assistantMessage);
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        state.sendStatus = 'failed';
        state.uploadProgress = 0;
        state.error = { message: action.payload as string };
      });

    // Approve Ticket
    builder
      .addCase(approveTicket.pending, (state, action) => {
        state.approveStatus = 'loading';
        // Track which specific ticket is being approved (messageId-ticketIndex)
        const { messageId, overrides } = action.meta.arg;
        const ticketIndex = overrides?.ticketIndex ?? 0;
        state.approvingTicketKey = `${messageId}-${ticketIndex}`;
      })
      .addCase(approveTicket.fulfilled, (state, action) => {
        state.approveStatus = 'success';
        state.approvingTicketKey = null;
        const msg = unwrap(action.payload);
        if (msg) {
          // Update the original message in place (it now has ticketCreated metadata)
          const idx = state.messages.findIndex(m => m.id === msg.id);
          if (idx !== -1) {
            state.messages[idx] = msg;
          }
        }
      })
      .addCase(approveTicket.rejected, (state, action) => {
        state.approveStatus = 'failed';
        state.approvingTicketKey = null;
        state.error = { message: action.payload as string };
      });

    // End Session
    builder
      .addCase(endChatSession.fulfilled, (state, action) => {
        const session = unwrap(action.payload);
        state.currentSession = session;
        const idx = state.sessions.findIndex(s => s.id === session.id);
        if (idx !== -1) {
          state.sessions[idx] = session;
        }
      });

    // Resume Session
    builder
      .addCase(resumeChatSession.pending, (state) => {
        state.resumeStatus = 'loading';
        state.error = null;
      })
      .addCase(resumeChatSession.fulfilled, (state, action) => {
        state.resumeStatus = 'success';
        const session = unwrap(action.payload);
        state.currentSession = session;
        const idx = state.sessions.findIndex(s => s.id === session.id);
        if (idx !== -1) {
          state.sessions[idx] = session;
        }
      })
      .addCase(resumeChatSession.rejected, (state, action) => {
        state.resumeStatus = 'failed';
        state.error = { message: action.payload as string };
      });

    // Delete Session
    builder
      .addCase(deleteChatSession.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.sessions = state.sessions.filter(s => s.id !== deletedId);
        if (state.currentSession?.id === deletedId) {
          state.currentSession = null;
          state.messages = [];
        }
      });

    // Update Persona
    builder
      .addCase(updateChatPersona.fulfilled, (state, action) => {
        const session = unwrap(action.payload);
        if (state.currentSession?.id === session.id) {
          state.currentSession = session;
        }
        const idx = state.sessions.findIndex((s: ChatSession) => s.id === session.id);
        if (idx !== -1) {
          state.sessions[idx] = session;
        }
      });
  },
});

// ==============================|| EXPORTS ||============================== //

export const {
  clearChatError,
  clearCurrentSession,
  clearChatState,
  setCurrentSession,
  mergePolledMessages,
  setUploadProgress,
  updateSessionStatus,
} = chatSlice.actions;

// Selectors
export const selectChatSessions = (state: any) => state.chat.sessions;
export const selectCurrentChatSession = (state: any) => state.chat.currentSession;
export const selectChatMessages = (state: any) => state.chat.messages;
export const selectChatStatus = (state: any) => state.chat.status;
export const selectChatSendStatus = (state: any) => state.chat.sendStatus;
export const selectChatApproveStatus = (state: any) => state.chat.approveStatus;
export const selectApprovingTicketKey = (state: any) => state.chat.approvingTicketKey;
export const selectChatResumeStatus = (state: any) => state.chat.resumeStatus;
export const selectUploadProgress = (state: any) => state.chat.uploadProgress;
export const selectChatError = (state: any) => state.chat.error;

export default chatSlice.reducer;
